import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import {
  GridState,
  GridStateCompositeFilterDescriptor,
  ITEM_FILTER_SOURCE,
  ItemFilterSource,
} from '../../models';
import { SelectableOption } from '../../mixins';
import { LoaderService, MessageService } from '../../services';
import { TokenService } from '@auth';

@Component({
  selector: 'app-item-filter',
  templateUrl: './item-filter.component.html',
  styleUrl: './item-filter.component.scss',
})
export class ItemFilterComponent implements OnInit {
  @Input() label: string = 'Items';
  @Input() preselected: number[] = [];
  @Input() showLegacyItems: boolean = false;

  @Output() selectionChanged: EventEmitter<number[]> = new EventEmitter<
    number[]
  >();

  @ViewChild('itemFilterAnchor', { read: ElementRef })
  anchor!: ElementRef;

  dropdownVisible: boolean = false;

  state!: GridState;
  data!: GridDataResult;
  includes: string = '';
  itemSearch: Subject<string> = new Subject<string>();
  selectableItems: SelectableOption<number>[] = [];

  masterSelected: number[] = [];
  appliedSelected: number[] = [];

  allSelected: boolean = false;
  pageSize: number = 20;

  private conceptKey?: string;

  constructor(
    private loaderService: LoaderService,
    private messageService: MessageService,
    private tokenService: TokenService,
    @Inject(ITEM_FILTER_SOURCE) private service: ItemFilterSource,
  ) {}

  async ngOnInit() {
    this.conceptKey = await this.tokenService.currentConceptKey();

    this.prepopulatePreselected();
    this.subscribeToAutocomplete();
    this.buildDefaultState();
    this.fetchItems();
  }

  get selectedCount(): number {
    return this.appliedSelected.length;
  }

  togglePanel() {
    this.dropdownVisible = !this.dropdownVisible;
  }

  prepopulatePreselected() {
    this.appliedSelected = [...this.preselected];
    this.masterSelected = [...this.preselected];
  }

  buildDefaultState() {
    this.state = new GridState(this.pageSize, [
      {field: 'categoryOrder', dir: 'asc'}, {field: 'sortOrder', dir: 'asc'}, {field: 'itemName', dir: 'asc'}
    ]);

    // Excluded items and items from other concepts should never show up here.
    this.state.filter = new GridStateCompositeFilterDescriptor().and({
      field: 'isExcluded',
      operator: 'eq',
      value: false,
    });

    if (this.conceptKey) {
      this.state.filter = this.state.filter.and({
        field: 'conceptKey',
        operator: 'eq',
        value: this.conceptKey,
      });
    }
  }

  fetchItems(state?: GridState) {
    if (!state) state = this.state;
    this.loaderService.show();
    this.service.getAllItems(state, this.showLegacyItems).subscribe({
      next: (result) => {
        this.data = result;
        this.selectableItems = this.data.data.map((item) => {
          const isSelected =
            this.allSelected || this.masterSelected.includes(item.id);
          return new SelectableOption<number>(
            item.id,
            `${item.itemName} (${item.parItemId}) ${item.isALaCarte ? 'ALC' : ''}`,
            isSelected,
          );
        });

        this.syncPendingSelection();
        this.syncAllSelected();

        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error(err);
        this.loaderService.hide();
      },
    });
  }

  
  syncPendingSelection() {
    this.selectableItems.forEach((item) => {
      if (item.selected) {
        if (!this.masterSelected.includes(item.value)) {
          this.masterSelected.push(item.value);
        }
      } else {
        this.masterSelected = this.masterSelected.filter(
          (selected) => selected !== item.value,
        );
      }
    });
  }

  handleFilter(item: string) {
    this.itemSearch.next(item);
  }

  handleScroll() {
    if (this.state.take! >= this.data.total) return;
    this.state.take! += this.pageSize;
    this.fetchItems(this.state);
  }

  subscribeToAutocomplete() {
    this.itemSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.includes = searchText;
          this.buildDefaultState();
          if (this.includes) {
            this.state.filter = this.state.filter?.and({
              field: 'itemName',
              ignoreCase: true,
              operator: 'contains',
              value: this.includes,
            });
          }

          this.fetchItems(this.state);
        }),
      )
      .subscribe();
  }

  toggleSelect(dataItem: SelectableOption<number>) {
    dataItem.selected = !dataItem.selected;

    // Unchecking a single item breaks "Select All", otherwise the next fetch would check it again
    if (!dataItem.selected) this.allSelected = false;

    this.syncPendingSelection();
  }

  toggleSelectAll() {
    this.allSelected = !this.allSelected;

    // Unchecking "Select All" has to drop every item, not only stop forcing them to checked
    if (!this.allSelected) this.masterSelected = [];

    this.state.skip = 0;
    this.state.take = this.data.total;
    this.fetchItems(this.state);
  }


  clearSelection() {
    this.allSelected = false;
    this.masterSelected = [];
    this.selectableItems.forEach((item) => (item.selected = false));

    this.applyFilter();
  }

  applyFilter() {
    this.appliedSelected = [...this.masterSelected];
    this.dropdownVisible = false;
    this.selectionChanged.emit(this.appliedSelected);
    this.resetSearch();
  }

  closePanel() {
    this.dropdownVisible = false;
    this.revertPending();
    this.resetSearch();
  }

  private revertPending() {
    this.allSelected = false;
    this.masterSelected = [...this.appliedSelected];
    this.selectableItems.forEach((item) => {
      item.selected = this.appliedSelected.includes(item.value);
    });
    this.syncAllSelected();
  }

  // Shows "Select All" checked when every item of the list is already selected (e.g. reopening the panel)
  private syncAllSelected() {
    if (this.allSelected || this.includes || !this.data) return;

    this.allSelected =
      this.data.total > 0 &&
      this.masterSelected.length >= this.data.total &&
      this.selectableItems.every((item) => item.selected);
  }

  private resetSearch() {
    if (!this.includes) return;
    this.includes = '';
    this.buildDefaultState();
    this.fetchItems();
  }
}
